import { Outlet, Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Waves, LayoutDashboard, BookOpen, Trophy, User, LogOut, Library, PenLine, Lightbulb, Mail, BookMarked, Briefcase, Mic, Bookmark, Timer, Palette, MessageSquare, LifeBuoy, MessageSquarePlus } from "lucide-react";
import StuckButton from "@/components/StuckButton";

export default function Layout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const isStudent = user?.role === "student";

  const links = [
    isStudent
      ? { to: "/student", label: "The Reef", icon: LayoutDashboard, testid: "nav-today" }
      : { to: "/parent", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
    { to: "/messages", label: "Messages", icon: MessageSquare, testid: "nav-messages" },
    { to: "/books", label: "Books", icon: BookMarked, testid: "nav-books" },
    ...(isStudent ? [
      { to: "/shelf", label: "My shelf", icon: Bookmark, testid: "nav-shelf" },
      { to: "/essay", label: "Essay coach", icon: PenLine, testid: "nav-essay" },
      { to: "/journal", label: "Journal", icon: Mic, testid: "nav-journal" },
      { to: "/passages", label: "Passages", icon: BookOpen, testid: "nav-passages" },
      { to: "/focus", label: "Focus", icon: Timer, testid: "nav-focus" },
      { to: "/help", label: "Help & Dictionary", icon: LifeBuoy, testid: "nav-help" },
    ] : []),
    { to: "/projects", label: "Projects", icon: Lightbulb, testid: "nav-projects" },
    { to: "/portfolio", label: "Portfolio", icon: Briefcase, testid: "nav-portfolio" },
    { to: "/library", label: "Library", icon: Library, testid: "nav-library" },
    ...(isStudent ? [{ to: "/achievements", label: "Badges", icon: Trophy, testid: "nav-achievements" }] : []),
    ...(isStudent ? [{ to: "/customize", label: "Customize", icon: Palette, testid: "nav-customize" }] : []),
    ...(!isStudent ? [{ to: "/digest", label: "Weekly digest", icon: Mail, testid: "nav-digest" }] : []),
    { to: "/feedback", label: "Feedback", icon: MessageSquarePlus, testid: "nav-feedback" },
    { to: "/profile", label: "Profile", icon: User, testid: "nav-profile" },
  ];

  const handleLogout = () => { logout(); nav("/login"); };

  return (
    <div className="min-h-screen bg-background flex flex-col md:flex-row">
      {/* Sidebar (desktop) */}
      <aside className="hidden md:flex md:w-64 md:flex-col border-r border-slate-200 bg-white p-6 gap-6">
        <Link to="/" data-testid="logo-link" className="flex items-center gap-2">
          <span className="grid place-items-center w-9 h-9 rounded-2xl bg-teal-700 text-white"><Waves size={18} strokeWidth={2.5} /></span>
          <span className="font-display text-xl font-bold text-slate-900">Oceanic Learn</span>
        </Link>
        <nav className="flex flex-col gap-1">
          {links.map((l) => (
            <NavLink key={l.to} to={l.to} data-testid={l.testid} end
              className={({isActive}) => `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors ${isActive ? "bg-teal-50 text-teal-800" : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"}`}>
              <l.icon size={18} strokeWidth={2.4} /> {l.label}
            </NavLink>
          ))}
        </nav>
        <div className="mt-auto flex flex-col gap-2">
          <div className="text-xs text-slate-500 px-3">Signed in as</div>
          <div className="px-3 pb-2">
            <div className="text-sm font-semibold text-slate-900" data-testid="sidebar-user-name">{user?.name}</div>
            <div className="text-xs text-slate-500">{user?.role}</div>
          </div>
          <button data-testid="logout-btn" onClick={handleLogout}
            className="flex items-center gap-2 px-3 py-2 rounded-xl text-sm text-slate-600 hover:bg-red-50 hover:text-red-700 transition-colors">
            <LogOut size={16} /> Log out
          </button>
        </div>
      </aside>

      {/* Mobile top bar */}
      <header className="md:hidden flex items-center justify-between px-4 py-3 border-b border-slate-200 bg-white sticky top-0 z-30">
        <Link to="/" className="flex items-center gap-2">
          <span className="grid place-items-center w-8 h-8 rounded-xl bg-teal-700 text-white"><Waves size={16} /></span>
          <span className="font-display font-bold">Oceanic Learn</span>
        </Link>
        <button data-testid="logout-btn-mobile" onClick={handleLogout} className="text-slate-500"><LogOut size={18} /></button>
      </header>

      <main className="flex-1 pb-24 md:pb-8">
        <div className="max-w-6xl mx-auto p-4 sm:p-6 md:p-10">
          <Outlet />
        </div>
      </main>

      {/* Mobile bottom nav */}
      <nav className="md:hidden fixed bottom-0 inset-x-0 bg-white border-t border-slate-200 px-2 py-2 flex justify-around z-30 overflow-x-auto">
        {links.slice(0, 5).map((l) => (
          <NavLink key={l.to} to={l.to} data-testid={`${l.testid}-mobile`} end
            className={({isActive}) => `flex flex-col items-center gap-0.5 text-[11px] font-medium px-3 py-1.5 rounded-xl shrink-0 ${isActive ? "text-teal-800 bg-teal-50" : "text-slate-500"}`}>
            <l.icon size={20} strokeWidth={2.4} /> {l.label}
          </NavLink>
        ))}
      </nav>

      {isStudent && <StuckButton />}
    </div>
  );
}
